import { useEffect, useState, useCallback } from "react";
import { api, errMsg, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, RotateCcw, ClipboardCopy, ShoppingCart, PackageCheck, Search, FileText, FileSpreadsheet } from "lucide-react";
import { Input } from "@/components/ui/input";

export const Procurement = () => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("to_order");
  const [q, setQ] = useState("");
  const [marking, setMarking] = useState("");

  const load = useCallback(async () => {
    try { const r = await api.get("/procurement"); setData(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const mark = async (line, ordered) => {
    setMarking(line.key);
    try {
      await api.post("/procurement/mark", { sku: line.sku, color: line.color, ordered });
      toast.success(ordered ? "Oznaczono jako zamówione" : "Przywrócono do listy");
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setMarking(""); }
  };

  const copyList = () => {
    const rows = data?.to_order || [];
    if (!rows.length) return toast.error("Brak pozycji do skopiowania");
    const txt = rows.map((l) => {
      const sizes = Object.entries(l.sizes || {}).map(([s, n]) => `${s}:${n}`).join(", ");
      return `${l.qty} szt. — ${l.name} (${l.sku})${l.color ? ` / ${l.color}` : ""}${sizes ? ` — ${sizes}` : ""}`;
    }).join("\n");
    navigator.clipboard.writeText(txt).then(() => toast.success("Skopiowano listę do schowka")).catch(() => toast.error("Nie udało się skopiować"));
  };

  if (!data) return <div className="flex items-center gap-2 text-muted-foreground py-10 justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-400" />Ładowanie listy zaopatrzenia…</div>;

  const s = data.summary || {};
  const rows = (tab === "to_order" ? data.to_order : data.ordered) || [];
  const needle = q.trim().toLowerCase();
  const list = needle ? rows.filter((l) => `${l.name} ${l.sku} ${l.color} ${l.brand} ${l.catalog_no}`.toLowerCase().includes(needle)) : rows;

  return (
    <div className="space-y-4" data-testid="procurement-view">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="font-mono text-xs text-amber-400 tracking-widest uppercase">Zaopatrzenie</p>
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">Lista zakupów do produkcji</h2>
          <p className="text-sm text-muted-foreground mt-1">Produkty z zamówień „Wysłane do produkcji", pogrupowane po modelu i kolorze.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="procurement-export-csv" onClick={() => downloadBlob("/procurement/export/csv", "zaopatrzenie.csv")} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-sm font-semibold hover:border-amber-400/60 hover:text-amber-400 transition-colors">
            <FileSpreadsheet className="h-4 w-4" />CSV
          </button>
          <button data-testid="procurement-export-pdf" onClick={() => downloadBlob("/procurement/export/pdf", "zaopatrzenie.pdf")} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-sm font-semibold hover:border-amber-400/60 hover:text-amber-400 transition-colors">
            <FileText className="h-4 w-4" />PDF
          </button>
          <button data-testid="procurement-copy" onClick={copyList} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-sm font-semibold hover:border-amber-400/60 hover:text-amber-400 transition-colors">
            <ClipboardCopy className="h-4 w-4" />Kopiuj listę
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1 w-fit" data-testid="procurement-tabs">
          <button data-testid="procurement-tab-to-order" onClick={() => setTab("to_order")} className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${tab === "to_order" ? "bg-amber-400 text-[#0D1117]" : "text-muted-foreground hover:text-foreground"}`}>
            <ShoppingCart className="h-3.5 w-3.5" />Do zamówienia ({s.to_order_lines || 0})
          </button>
          <button data-testid="procurement-tab-ordered" onClick={() => setTab("ordered")} className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${tab === "ordered" ? "bg-amber-400 text-[#0D1117]" : "text-muted-foreground hover:text-foreground"}`}>
            <PackageCheck className="h-3.5 w-3.5" />Zamówione ({s.ordered_lines || 0})
          </button>
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="procurement-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj: nazwa, SKU, kolor, marka…" className="pl-9 bg-card" />
        </div>
      </div>

      {tab === "to_order" && (
        <p className="text-sm text-muted-foreground" data-testid="procurement-summary">Razem do zamówienia: <span className="font-bold text-foreground">{s.to_order_qty || 0} szt.</span> w {s.to_order_lines || 0} pozycjach.</p>
      )}

      {list.length === 0 ? (
        <div data-testid="procurement-empty" className="rounded-xl border border-dashed border-border bg-card/40 py-12 text-center text-muted-foreground">
          {tab === "to_order" ? "Brak produktów do zamówienia. Wszystko zamówione lub brak zamówień wysłanych do produkcji." : "Nic jeszcze nie oznaczono jako zamówione."}
        </div>
      ) : (
        <div className="space-y-1.5">
          {list.map((l) => (
            <div key={l.key} data-testid={`procurement-line-${l.sku}-${l.color}`} className="rounded-lg border border-border bg-card px-3 py-2 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-tight">
                  <span className="font-semibold truncate">{l.name}</span>
                  <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">{l.sku}</span>
                  {l.brand && <span className="rounded bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-400">{l.brand}</span>}
                  <span className="text-muted-foreground">
                    {l.color && <>· <span className="font-semibold text-foreground">{l.color}</span> </>}
                    · <span className="font-bold text-foreground">{l.qty} szt.</span>
                  </span>
                  {Object.entries(l.sizes || {}).map(([sz, n]) => (
                    <span key={sz} className="inline-flex items-center gap-0.5 rounded border border-border bg-secondary/40 px-1.5 py-0.5 text-[11px]"><span className="font-bold">{sz}</span><span className="text-muted-foreground">×{n}</span></span>
                  ))}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 flex flex-wrap gap-x-2 gap-y-1">
                  <span className="font-semibold">Z zamówień:</span>
                  {l.orders.map((o, i) => (
                    <span key={i} className="whitespace-nowrap">
                      {o.number}{o.customer ? <> · <span className="text-foreground/80">{o.customer}</span></> : ""}
                      {o.status ? <span className="ml-1 rounded bg-secondary px-1 py-0.5 text-[9px] uppercase tracking-wide text-amber-400/90">{o.status}</span> : ""} ({o.qty} szt.)
                    </span>
                  ))}
                  {tab === "ordered" && l.procured_at && <span className="text-emerald-400">· zamówione {new Date(l.procured_at).toLocaleDateString("pl-PL")}</span>}
                </div>
              </div>
              <div className="shrink-0">
                {tab === "to_order" ? (
                  <button data-testid={`procurement-mark-${l.sku}-${l.color}`} disabled={marking === l.key} onClick={() => mark(l, true)} title="Oznacz jako zamówione" className="inline-flex items-center gap-1.5 rounded-md bg-amber-400 px-2.5 py-1.5 text-xs font-bold text-[#0D1117] hover:bg-amber-300 disabled:opacity-50 transition-colors">
                    {marking === l.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}<span className="hidden md:inline">Zamówione</span>
                  </button>
                ) : (
                  <button data-testid={`procurement-undo-${l.sku}-${l.color}`} disabled={marking === l.key} onClick={() => mark(l, false)} title="Cofnij" className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold hover:border-amber-400/60 hover:text-amber-400 disabled:opacity-50 transition-colors">
                    {marking === l.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}<span className="hidden md:inline">Cofnij</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
